/**
 * Randomization: whether the study is randomized, its method and its
 * stratification factors. ONE rule, read by the study profile and by every
 * generator prompt that mentions randomization.
 *
 * Only the protocol or a person supplies a method or a factor. Protocol
 * analysis used to save method "Block Randomization" and stratification
 * ["Site"] on every study whose protocol stated neither, and the Study Design
 * page offered ["Site", "Age Group"]; nothing records whether a person ever
 * changed them. A stored value matching one of those platform defaults is
 * therefore read as not stated (fixed at read time, never by backfill).
 */

import type { Fact } from "./types";
import { parseRandomized, text, triState } from "./parse";

type Rec = Record<string, unknown>;
const obj = (v: unknown): Rec => (v && typeof v === "object" && !Array.isArray(v) ? (v as Rec) : {});

/** Method and factors the platform used to store or offer without any source. */
export const PLATFORM_DEFAULT_METHOD = "Block Randomization";
const PLATFORM_DEFAULT_FACTOR_SETS = [["site"], ["age group", "site"]];

export interface RandomizationInputs {
  /** projects/{id}.studySetup */
  studySetup?: Rec | null;
  /** projects/{id}/protocolAnalysis/latest .analysis */
  analysis?: Rec | null;
  /** projects/{id}/studyDesign/config */
  studyDesign?: Rec | null;
}

export interface RandomizationDetails {
  method: Fact<string>;
  stratificationFactors: Fact<string[]>;
}

const RANDOMIZED_NEEDED = "Whether the study is randomized";

/**
 * Whether the study is randomized. Study Settings wins; else explicit protocol
 * text. Study Design's switch is NOT evidence on its own: the protocol agent
 * writes it from an exact match on "randomized" and the page's default is on,
 * so it counts only when it agrees with the protocol, and a disagreement in
 * either direction is TBD with a note.
 */
export function resolveRandomized(input: RandomizationInputs): Fact<boolean> {
  const ss = obj(input.studySetup);
  const userRandomized = triState(ss.randomized === "unknown" ? null : ss.randomized);
  const sdRand = obj(obj(input.studyDesign).randomization);
  const sdEnabled = typeof sdRand.enabled === "boolean" ? sdRand.enabled : null;
  const aDesign = obj(obj(input.analysis).studyDesign);
  const protocolRandomized = parseRandomized(aDesign.randomization);
  if (userRandomized !== null) return { value: userRandomized, source: "user", needed: RANDOMIZED_NEEDED };
  if (protocolRandomized !== null && sdEnabled !== null && sdEnabled !== protocolRandomized) {
    return {
      value: null,
      source: null,
      needed: `Confirm randomization in Study Settings: the protocol says ${protocolRandomized ? "randomized" : "not randomized"} but Study Design has randomization turned ${sdEnabled ? "on" : "off"}`,
    };
  }
  if (protocolRandomized !== null) {
    const evidence = text(aDesign.randomization) ?? undefined;
    return { value: protocolRandomized, source: "protocol", needed: RANDOMIZED_NEEDED, ...(evidence ? { evidence } : {}) };
  }
  return { value: null, source: null, needed: RANDOMIZED_NEEDED };
}

function list(v: unknown): string[] {
  if (!Array.isArray(v)) return [];
  const out: string[] = [];
  for (const x of v) {
    const s = typeof x === "string" ? x.trim() : "";
    if (s && !out.some((o) => o.toLowerCase() === s.toLowerCase())) out.push(s);
  }
  return out;
}

const sameSet = (a: string[], b: string[]) =>
  a.length === b.length && [...a].map((s) => s.toLowerCase()).sort().join("|") === [...b].map((s) => s.toLowerCase()).sort().join("|");

/**
 * A "method" that is really the protocol's yes/no answer. The protocol agent
 * stored `analysis.studyDesign.randomization` as the method, so most stored
 * records say "randomized" or "non-randomized" (measured on Clinical Dev,
 * 2026-10-26: 53 of 53 studies, every one with factors ["Site"]).
 */
const STATUS_WORD = /^(?:non[-\s]?randomi[sz]ed|not\s+randomi[sz]ed|randomi[sz]ed|n\/?a|none|unknown|yes|no)$/i;

const isDefaultFactorSet = (factors: string[]) => PLATFORM_DEFAULT_FACTOR_SETS.some((d) => sameSet(factors, d));

/** The method a person set on Study Design, or null: never a yes/no word or the platform default. */
export function storedMethodStated(sdRandomization: unknown): string | null {
  const r = obj(sdRandomization);
  const method = text(r.method);
  if (!method || STATUS_WORD.test(method)) return null;
  if (method === PLATFORM_DEFAULT_METHOD && isDefaultFactorSet(list(r.stratificationFactors))) return null;
  return method;
}

/** The stratification factors a person set on Study Design: never the platform's "Site" / "Site, Age Group". */
export function storedFactorsStated(sdRandomization: unknown): string[] {
  const factors = list(obj(sdRandomization).stratificationFactors);
  return isDefaultFactorSet(factors) ? [] : factors;
}

/**
 * The stored Study Design randomization states nothing a person chose. A person
 * who picked exactly Block Randomization stratified by Site is
 * indistinguishable from the default and reads as not stated.
 */
export function isPlatformDefaultRandomization(sdRandomization: unknown): boolean {
  return storedMethodStated(sdRandomization) === null && storedFactorsStated(sdRandomization).length === 0;
}

/**
 * Method and factors for a study whose randomization is known. Undefined when
 * it is not known: there is then nothing to state, and the randomized fact
 * already carries the open item.
 */
export function resolveRandomizationDetails(input: {
  randomized: boolean | null;
  analysis?: Rec | null;
  studyDesign?: Rec | null;
}): RandomizationDetails | undefined {
  if (input.randomized === null) return undefined;
  if (input.randomized === false) {
    return {
      method: { value: "Not applicable", source: "derived" },
      stratificationFactors: { value: [], source: "derived" },
    };
  }
  const a = obj(input.analysis);
  // What the protocol's TEXT says about the method (operationsFacts.randomization,
  // operationsFromProtocol.ts version 6, 2026-10-30, HVU-SLP-07: "simple
  // (unstratified) randomization in permuted blocks of 4" printed "the protocol
  // does not state it"). Deterministic and quoted; the model-extracted lists
  // below do not outrank an explicit "unstratified".
  const read = obj(obj(a.operationsFacts).randomization);
  const readValue = obj(read.value);
  const readEvidence = text(read.evidence) ?? undefined;
  const readMethod = readValue.kind ? text(readValue.label) : null;
  const statedUnstratified = readValue.stratified === false;
  const protocolFactors = statedUnstratified
    ? []
    : list([
        ...list(a.stratificationFactors),
        ...list(obj(a.studyDesign).stratificationFactors),
        ...list(obj(a.randomization).stratificationFactors),
        ...list(readValue.factors),
      ]);
  const sdRand = obj(obj(input.studyDesign).randomization);
  const storedMethod = storedMethodStated(sdRand);
  const storedFactors = storedFactorsStated(sdRand);

  const method: Fact<string> = storedMethod
    ? { value: storedMethod, source: "user" }
    : readMethod
      ? { value: readMethod, source: "protocol", ...(readEvidence ? { evidence: readEvidence } : {}) }
      : { value: null, source: null, needed: "Randomization method (the protocol does not state it)" };

  let stratificationFactors: Fact<string[]>;
  if (storedFactors.length && !sameSet(storedFactors, protocolFactors)) {
    stratificationFactors = { value: storedFactors, source: "user" };
  } else if (protocolFactors.length) {
    stratificationFactors = { value: protocolFactors, source: "protocol" };
  } else if (statedUnstratified) {
    stratificationFactors = { value: [], source: "protocol", ...(readEvidence ? { evidence: readEvidence } : {}) };
  } else {
    stratificationFactors = {
      value: null,
      source: null,
      needed: "Stratification factors (the protocol states none; confirm whether randomization is stratified)",
    };
  }
  return { method, stratificationFactors };
}

/**
 * The Study Design randomization protocol analysis stores: whether the
 * protocol says randomized, and a method or factors only when a person set them
 * (kept across re-analysis) or, for factors, the protocol states them. Never a
 * platform default.
 */
export function randomizationToStore(
  analysis: object | null | undefined,
  stored: unknown,
): { enabled: boolean; method: string; stratificationFactors: string[] } {
  const a = obj(analysis);
  // The same exact match the agent always used ("Randomized 2:1" stores off;
  // resolveRandomized never treats the switch alone as evidence).
  const enabled = obj(a.studyDesign).randomization === "randomized";
  const storedMethod = storedMethodStated(stored) ?? "";
  const storedFactors = storedFactorsStated(stored);
  if (storedMethod || storedFactors.length) {
    return { enabled, method: storedMethod, stratificationFactors: storedFactors.length ? storedFactors : list(a.stratificationFactors) };
  }
  return { enabled, method: "", stratificationFactors: list(a.stratificationFactors) };
}

/** The RANDOMIZATION block of a generator prompt. */
export function randomizationPromptText(randomized: boolean | null, details: RandomizationDetails | undefined): string {
  if (randomized === false) return "RANDOMIZATION: The study is not randomized. Do not describe a randomization scheme.";
  if (randomized === null || !details) {
    return "RANDOMIZATION: Whether the study is randomized is not confirmed (TBD). Do not describe a randomization method, block size or stratification factor.";
  }
  const method = details.method.value ?? "not stated in the protocol (TBD)";
  const f = details.stratificationFactors;
  const factors = f.value?.length
    ? f.value.join(", ")
    : f.value && f.source === "protocol"
      ? "none (the protocol states the randomization is not stratified)"
      : "none stated in the protocol (TBD)";
  return (
    `RANDOMIZATION: The study is randomized. Randomization method: ${method}. Stratification factors: ${factors}. ` +
    "Do not state a randomization method, block size or stratification factor that is not given here."
  );
}

/** The same block for a generator that has the stored records but no study profile. */
export function randomizationPromptFromStored(input: RandomizationInputs): string {
  const randomized = resolveRandomized(input).value;
  return randomizationPromptText(
    randomized,
    resolveRandomizationDetails({ randomized, analysis: input.analysis, studyDesign: input.studyDesign }),
  );
}
